import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { Check, ChevronsUpDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CLOSED_JOB_STATUSES_FILTER } from "@/utils/jobs/status";

interface Job {
	id: string;
	title: string;
	company_name?: string;
	status?: string;
	seniority_level?: string;
	location_type?: string;
}

interface JobSelectorProps {
	onSelectJob: (job: Job) => void;
	selectedJobId?: string;
	buttonClassName?: string;
	disabled?: boolean;
	companyId?: string;
	/** When true, closed jobs are shown without needing the toggle. Defaults to false (active-only). */
	includeClosedByDefault?: boolean;
	/** Hide the "Include closed jobs" toggle entirely (forces the includeClosedByDefault behavior). */
	hideClosedToggle?: boolean;
}

const statusBadgeVariant = (status?: string): "success" | "warning" | "secondary" | "outline" => {
	switch (status) {
		case "open": return "success";
		case "hold": return "warning";
		case "closed/won":
		case "closed/lost": return "secondary";
		default: return "outline";
	}
};

export function JobSelector({
	onSelectJob,
	selectedJobId,
	buttonClassName,
	disabled = false,
	companyId,
	includeClosedByDefault = false,
	hideClosedToggle = false
}: JobSelectorProps) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [jobs, setJobs] = useState<Job[]>([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [selectedJobTitle, setSelectedJobTitle] = useState("");
	const [internalSelectedJobId, setInternalSelectedJobId] = useState("");
	const [includeClosed, setIncludeClosed] = useState(includeClosedByDefault);

	const selectedJob = jobs.find(job => job.id === selectedJobId);

	useEffect(() => {
		if (open)
			fetchJobs();
	}, [open, companyId, searchTerm, includeClosed]);

	const fetchJobs = async () => {
		setIsLoading(true);
		setError(null);

		try {
			let query = supabase
				.from('jobs')
				.select('id, title, status, seniority_level, location_type, company:company_id (name)');

			if (searchTerm)
				query = query.ilike('title', `%${searchTerm}%`);
			else
				query = query.order('title', { ascending: true });

			if (companyId)
				query = query.eq('company_id', companyId);

			if (!includeClosed)
				query = query.not('status', 'in', CLOSED_JOB_STATUSES_FILTER);

			query = query.limit(20);

			const { data, error } = await query;

			if (error)
				throw error;

			const formattedJobs = (data as any[]).map(job => ({
				id: job.id,
				title: job.title,
				status: job.status,
				seniority_level: job.seniority_level,
				location_type: job.location_type,
				company_name: job.company ? job.company.name : undefined
			}));

			setJobs(formattedJobs);

		} catch (err) {
			console.error('Error fetching jobs:', err);
			setError(t("jobs.selector.error", "Failed to load jobs."));
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					disabled={disabled}
					role="combobox"
					aria-expanded={open}
					className={cn("justify-between", buttonClassName)}
				>
					{selectedJobTitle || selectedJob?.title || t("jobs.selector.selectPlaceholder", "Select job...")}
					<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-[480px] p-0">
				<Command filter={() => 1}>
					<CommandInput
						placeholder={t("jobs.selector.searchPlaceholder", "Search jobs...")}
						value={searchTerm}
						onValueChange={setSearchTerm}
					/>
					{!hideClosedToggle && (
						<div className="flex items-center justify-between gap-2 px-3 py-2 border-b">
							<span className="text-xs text-muted-foreground">
								{includeClosed
									? t("jobs.selector.showingAll", "Showing all jobs")
									: t("jobs.selector.showingActive", "Showing active jobs")}
							</span>
							<label className="flex items-center gap-2 text-xs cursor-pointer select-none">
								<Checkbox
									checked={includeClosed}
									onCheckedChange={(v) => setIncludeClosed(v === true)}
								/>
								{t("jobs.selector.includeClosed", "Include closed jobs")}
							</label>
						</div>
					)}
					<div className="max-h-[320px] overflow-y-auto" onWheel={(e) => e.stopPropagation()}>
						<CommandList>
							{isLoading ? (
								<div className="py-6 text-center text-sm text-gray-500">
									{t("jobs.selector.loading", "Loading jobs...")}
								</div>
							) : error ? (
								<div className="py-6 text-center text-sm text-red-500">
									{error}
								</div>
							) : jobs.length === 0 ? (
								<CommandEmpty>{t("jobs.selector.noResults", "No jobs found.")}</CommandEmpty>
							) : (
								<CommandGroup>
									{jobs.map((job) => {
										const meta = [job.seniority_level, job.location_type].filter(Boolean).join(" · ");
										return (
											<CommandItem
												key={job.id}
												value={job.id}
												onSelect={() => {
													onSelectJob(job);
													setSelectedJobTitle(job.title);
													setInternalSelectedJobId(job.id);
													setSearchTerm("");
													setOpen(false);
												}}
											>
												<Check
													className={cn(
														"mr-2 h-4 w-4 shrink-0",
														internalSelectedJobId === job.id ? "opacity-100" : "opacity-0"
													)}
												/>
												<div className="flex flex-col gap-0.5 flex-1 min-w-0">
													<div className="flex items-center gap-2 min-w-0">
														<span className="font-medium truncate">{job.title}</span>
														{job.status && (
															<Badge variant={statusBadgeVariant(job.status)} className="shrink-0">
																{job.status}
															</Badge>
														)}
													</div>
													{job.company_name && (
														<span className="text-xs text-muted-foreground truncate">{job.company_name}</span>
													)}
													{meta && (
														<span className="text-xs text-muted-foreground truncate">{meta}</span>
													)}
												</div>
											</CommandItem>
										);
									})}
								</CommandGroup>
							)}
						</CommandList>
					</div>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
